/**
 * Category and subcategory constants for TutorMed
 * This file serves as the single source of truth for all category-related data
 * Matches the backend subcategory definitions
 */

// Main categories with their display names
export const MAIN_CATEGORIES = {
  nurse: 'Nurse',
  physician_assistant: 'Physician Assistant',
  medical_student: 'Medical Student',
  doctor: 'Medical Doctor',
  foreign_exam: 'Foreign Exam Prep',
  gmdc_candidate: 'GMDC Candidate',
  lab_science: 'Lab Science',
  midwifery: 'Midwifery'
};

// Subcategories matching backend User model
export const SUBCATEGORIES = {
  // Nurse subcategories
  nurse_course_only: 'Nurse - Course Only',
  nurse_exam_only: 'Nurse - Exam Only',
  nurse_course_exam: 'Nurse - Course + Exam',
  
  // Physician Assistant subcategories
  physician_assistant_course_only: 'Physician Assistant - Course Only',
  physician_assistant_exam_only: 'Physician Assistant - Exam Only',
  physician_assistant_course_exam: 'Physician Assistant - Course + Exam',
  
  // Medical Student subcategories
  medical_student_course_only: 'Medical Student - Course Only',
  medical_student_exam_only: 'Medical Student - Exam Only',
  medical_student_course_exam: 'Medical Student - Course + Exam',
  
  // Doctor subcategories
  doctor_course_only: 'Doctor - Course Only',
  doctor_exam_only: 'Doctor - Exam Only',
  doctor_course_exam: 'Doctor - Course + Exam',
  
  // Foreign Exam subcategories
  foreign_exam_course_only: 'Foreign Exam - Course Only',
  foreign_exam_exam_only: 'Foreign Exam - Exam Only',
  foreign_exam_course_exam: 'Foreign Exam - Course + Exam',
  
  // GMDC Candidate subcategories
  gmdc_candidate_full_course: 'GMDC Candidate - Full Course',
  gmdc_candidate_osce_course_exam: 'GMDC Candidate - OSCE Course + Exam',
  gmdc_candidate_mcq_course_exam: 'GMDC Candidate - MCQ Course + Exam',
  gmdc_candidate_osce_course_only: 'GMDC Candidate - OSCE Course Only',
  gmdc_candidate_mcq_course_only: 'GMDC Candidate - MCQ Course Only',
  gmdc_candidate_mcq_osce_course_only: 'GMDC Candidate - MCQ Course + OSCE Course Only',
  gmdc_candidate_mcq_exam_only: 'GMDC Candidate - MCQ Exam Only',
  gmdc_candidate_osce_exam_only: 'GMDC Candidate - OSCE Exam Only',
  
  // Lab Science subcategories
  lab_science_course_only: 'Lab Science - Course Only',
  lab_science_exam_only: 'Lab Science - Exam Only',
  lab_science_course_exam: 'Lab Science - Course + Exam',
  
  // Midwifery subcategories
  midwifery_course_only: 'Midwifery - Course Only',
  midwifery_exam_only: 'Midwifery - Exam Only',
  midwifery_course_exam: 'Midwifery - Course + Exam',
};

// Grouped subcategories by main category
export const SUBCATEGORIES_BY_CATEGORY = {
  nurse: ['nurse_course_only', 'nurse_exam_only', 'nurse_course_exam'],
  physician_assistant: ['physician_assistant_course_only', 'physician_assistant_exam_only', 'physician_assistant_course_exam'],
  medical_student: ['medical_student_course_only', 'medical_student_exam_only', 'medical_student_course_exam'],
  doctor: ['doctor_course_only', 'doctor_exam_only', 'doctor_course_exam'],
  foreign_exam: ['foreign_exam_course_only', 'foreign_exam_exam_only', 'foreign_exam_course_exam'],
  gmdc_candidate: [
    'gmdc_candidate_full_course', 'gmdc_candidate_osce_course_exam', 'gmdc_candidate_mcq_course_exam',
    'gmdc_candidate_osce_course_only', 'gmdc_candidate_mcq_course_only', 'gmdc_candidate_mcq_osce_course_only',
    'gmdc_candidate_mcq_exam_only', 'gmdc_candidate_osce_exam_only'
  ],
  lab_science: ['lab_science_course_only', 'lab_science_exam_only', 'lab_science_course_exam'],
  midwifery: ['midwifery_course_only', 'midwifery_exam_only', 'midwifery_course_exam']
};

// Legacy category mapping (for backward compatibility)
export const LEGACY_CATEGORY_MAPPING = {
  'pa': 'physician_assistant',
  'student': 'medical_student',
  'foreign': 'foreign_exam'
};

// Function to get all subcategories for a main category
export function getSubcategoriesForCategory(category) {
  // Handle legacy categories
  const mappedCategory = LEGACY_CATEGORY_MAPPING[category] || category;
  return SUBCATEGORIES_BY_CATEGORY[mappedCategory] || [];
}

// Function to get display name for a subcategory
export function getSubcategoryDisplayName(subcategory) {
  return SUBCATEGORIES[subcategory] || subcategory;
}

// Function to get main category from a subcategory
export function getMainCategoryFromSubcategory(subcategory) {
  for (const [category, subcats] of Object.entries(SUBCATEGORIES_BY_CATEGORY)) {
    if (subcats.includes(subcategory)) {
      return category;
    }
  }
  return null;
}

// Function to get display name for a main category
export function getCategoryDisplayName(category) {
  // Handle legacy categories
  const mappedCategory = LEGACY_CATEGORY_MAPPING[category] || category;
  return MAIN_CATEGORIES[mappedCategory] || category;
}

// Get all subcategory options (for select dropdowns)
export function getAllSubcategoryOptions() {
  return Object.entries(SUBCATEGORIES).map(([value, label]) => ({
    value,
    label,
    category: getMainCategoryFromSubcategory(value)
  }));
}

// Get grouped subcategory options (for grouped select dropdowns)
export function getGroupedSubcategoryOptions() {
  const groups = [];
  
  for (const [category, displayName] of Object.entries(MAIN_CATEGORIES)) {
    const subcategories = SUBCATEGORIES_BY_CATEGORY[category];
    if (subcategories && subcategories.length > 0) {
      groups.push({
        label: displayName,
        options: subcategories.map(subcat => ({
          value: subcat,
          label: SUBCATEGORIES[subcat]
        }))
      });
    }
  }
  
  return groups;
}

// Export default category configuration
export default {
  MAIN_CATEGORIES,
  SUBCATEGORIES,
  SUBCATEGORIES_BY_CATEGORY,
  LEGACY_CATEGORY_MAPPING,
  getSubcategoriesForCategory,
  getSubcategoryDisplayName,
  getMainCategoryFromSubcategory,
  getCategoryDisplayName,
  getAllSubcategoryOptions,
  getGroupedSubcategoryOptions
};